import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import type { ReactNode } from 'react';
import { Select } from 'antd';
import { CheckCircleIcon, StepOrgUserIcon, PlusIcon, TrashIcon } from '../../assets/icons';
import Breadcrumbs from '../../components/Breadcrumbs';
import TreeDropdown from '../../components/TreeDropdown';
import OrgNodeTree, { orgNodeLabels, keyId, nodeKey } from '../../components/OrgNodeTree';
import { useGetRolesQuery } from '../../features/roles/rolesApi';
import {
  useGetOrganizationsQuery,
  useGetNodeTreeQuery,
} from '../../features/organizations/organizationsApi';
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetUserQuery,
  userName,
  userEmail,
  userPhone,
  userActive,
} from '../../features/users/usersApi';

const FIELD =
  'w-full rounded-lg border bg-white px-4 py-3.5 text-base text-ink placeholder:text-[#6b7280] transition-colors focus:outline-none focus:ring-2';
const fieldCls = (invalid?: string) =>
  [
    FIELD,
    invalid
      ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
      : 'border-line focus:border-brand focus:ring-brand/15',
  ].join(' ');

interface RoleRow {
  role: number | '';
  /** OrgNodeTree selection keys (node/booth), e.g. "n:15" / "a:11". */
  orgNodes: string[];
}
interface UserFormValues {
  fullName: string;
  email: string;
  contact: string;
  organization: number | '';
  roles: RoleRow[];
  active: boolean;
}

const validationSchema = Yup.object({
  fullName: Yup.string().trim().required('Full name is required'),
  email: Yup.string().trim().required('Email is required').email('Enter a valid email address'),
  contact: Yup.string()
    .trim()
    .required('Contact number is required')
    .matches(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number'),
  organization: Yup.mixed().test('required', 'Select an organization', (v) => v !== '' && v != null),
  roles: Yup.array()
    .of(
      Yup.object({
        role: Yup.mixed().test('required', 'Select a role', (v) => v !== '' && v != null),
        orgNodes: Yup.array().min(1, 'Select at least one level'),
      }),
    )
    .min(1, 'Add at least one role'),
});

function Field({
  label,
  htmlFor,
  error,
  required,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={htmlFor} className="text-sm font-semibold text-ink-900">
        {label}
        {required && <span className="text-[#ba1a1a]"> *</span>}
      </label>
      {children}
      {error && <span className="text-xs font-medium text-[#ba1a1a]">{error}</span>}
    </div>
  );
}

export default function CreateUserPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);

  const { data: rolesData } = useGetRolesQuery({ all: true });
  const { data: orgsData } = useGetOrganizationsQuery({ all: true });
  const { data: editUser } = useGetUserQuery(editId!, { skip: !isEdit });
  // org node id → name from the edit response (so selected Org Levels show names even
  // when the org's nodes aren't fetched, e.g. organization is null).
  const [nodeNames, setNodeNames] = useState<Record<string, string>>({});
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const saving = isCreating || isUpdating;

  const formik = useFormik<UserFormValues>({
    initialValues: {
      fullName: '',
      email: '',
      contact: '',
      organization: '',
      roles: [{ role: '', orgNodes: [] }],
      active: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      // One entry per role, with its selected org nodes as an array.
      const roles = values.roles
        .filter((r) => r.role !== '' && r.orgNodes.length > 0)
        .map((r) => ({ role: Number(r.role), org_level: r.orgNodes.map(keyId) }));
      const organization = Number(values.organization);
      try {
        if (isEdit && editId) {
          await updateUser({
            id: editId,
            patch: {
              fullName: values.fullName.trim(),
              email: values.email.trim(),
              contact: values.contact.trim(),
              organization,
              is_active: values.active,
              ...(roles.length > 0 ? { roles } : {}),
            },
          }).unwrap();
        } else {
          await createUser({
            fullName: values.fullName.trim(),
            email: values.email.trim(),
            contact: values.contact.trim(),
            organization,
            roles,
            active: values.active,
          }).unwrap();
        }
        navigate('/admin/users');
      } catch {
        /* surfaced below via submit error */
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;

  // Edit mode: prefill the form once the user loads (scalars + org + role rows).
  useEffect(() => {
    if (!editUser) return;
    setFieldValue('fullName', userName(editUser) === '—' ? '' : userName(editUser));
    setFieldValue('email', userEmail(editUser));
    setFieldValue('contact', userPhone(editUser).replace(/\D/g, '').slice(-10));
    setFieldValue('active', userActive(editUser));
    if (editUser.organization != null) setFieldValue('organization', editUser.organization);

    const names: Record<string, string> = {};
    const rows: RoleRow[] = (editUser.roles ?? [])
      .filter((r): r is Exclude<typeof r, string> => typeof r === 'object')
      .map((r) => {
        const roleId = typeof r.role === 'object' ? r.role?.id : r.role;
        const ol = r.org_level;
        const ids = Array.isArray(ol) ? ol.map(Number) : ol != null ? [Number(ol)] : [];
        // Saved org_level are node ids → tree keys; capture names for the labels.
        const orgNodes = ids.map((id) => nodeKey(id));
        ids.forEach((id, idx) => {
          const nm = r.org_level_names?.[idx];
          if (nm) names[nodeKey(id)] = nm;
        });
        return { role: roleId != null ? Number(roleId) : '', orgNodes };
      });
    setNodeNames(names);
    if (rows.length > 0) setFieldValue('roles', rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editUser]);

  const roles = rolesData?.items ?? [];
  const orgs = orgsData?.items ?? [];
  // The selected organization's node tree drives the Org Level picker.
  const { data: orgTree } = useGetNodeTreeQuery(
    { organization: values.organization as number },
    { skip: values.organization === '' },
  );
  // key → label for showing selected levels (tree labels + names from the edit response).
  const orgNodeNameMap: Record<string, string> = {
    ...nodeNames,
    ...orgNodeLabels(orgTree ?? []),
  };

  const errorOf = (name: keyof UserFormValues) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;

  // Per-row errors reveal after a submit attempt.
  const rowError = (i: number, field: 'role' | 'orgNodes'): string | undefined => {
    if (formik.submitCount === 0) return undefined;
    const rolesErr = formik.errors.roles;
    const rErr = Array.isArray(rolesErr) ? rolesErr[i] : undefined;
    if (rErr && typeof rErr === 'object') return (rErr as Record<string, string | undefined>)[field];
    return undefined;
  };

  const setRow = (i: number, patch: Partial<RoleRow>) =>
    setFieldValue(
      'roles',
      values.roles.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    );
  const addRow = () => setFieldValue('roles', [...values.roles, { role: '', orgNodes: [] }]);
  const removeRow = (i: number) => {
    // Removing the only row would leave none — clear its values instead.
    if (values.roles.length === 1) {
      setFieldValue('roles', [{ role: '', orgNodes: [] }]);
      return;
    }
    setFieldValue(
      'roles',
      values.roles.filter((_, idx) => idx !== i),
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb + heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[{ label: 'Users', to: '/admin/users' }, { label: isEdit ? 'Edit User' : 'Create New User' }]}
        />
        <h1 className="pt-1 text-[32px] font-bold leading-10 tracking-[-0.64px] text-ink-900">
          {isEdit ? 'Edit User' : 'Create New User'}
        </h1>
        <p className="text-base text-ink-secondary">
          Register a new administrator or regional officer to the CHUNAAV platform.
        </p>
      </div>

      {/* Form card */}
      <form
        onSubmit={formik.handleSubmit}
        noValidate
        className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_4px_6px_-2px_rgba(0,0,0,0.05)]"
      >
        {/* Card header */}
        <div className="flex items-center justify-between border-b border-line/30 bg-brand-shade px-6 py-6">
          <div className="flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-lg bg-[#dbeafe] text-brand">
              <StepOrgUserIcon className="h-[18px] w-[25px]" />
            </span>
            <h2 className="text-xl font-semibold tracking-[-0.2px] text-ink-900">User Details</h2>
          </div>
        </div>

        {/* Form body */}
        <div className="flex flex-col gap-8 p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field label="Full Name" htmlFor="fullName" required error={errorOf('fullName')}>
              <input
                id="fullName"
                name="fullName"
                placeholder="e.g. Rajesh Kumar"
                value={values.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={fieldCls(errorOf('fullName'))}
              />
            </Field>

            <Field label="Email Address" htmlFor="email" required error={errorOf('email')}>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="rajesh.k@chunaav.gov.in"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={fieldCls(errorOf('email'))}
              />
            </Field>

            <Field label="Contact Number" htmlFor="contact" required error={errorOf('contact')}>
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-ink-secondary">
                  +91
                </span>
                <input
                  id="contact"
                  name="contact"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="98765 43210"
                  value={values.contact}
                  onChange={(e) => setFieldValue('contact', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  onBlur={handleBlur}
                  className={[fieldCls(errorOf('contact')), 'pl-[57px]'].join(' ')}
                />
              </div>
            </Field>

            <Field label="Organization" htmlFor="organization" required error={errorOf('organization')}>
              <Select
                className="w-full"
                placeholder="Select Organization"
                value={values.organization === '' ? undefined : values.organization}
                onChange={(v) => {
                  setFieldValue('organization', v);
                  // Clear picked org nodes — they belong to the previous org.
                  setFieldValue(
                    'roles',
                    values.roles.map((r) => ({ ...r, orgNodes: [] })),
                  );
                }}
                options={orgs.map((o) => ({ value: o.id, label: o.name }))}
              />
            </Field>
          </div>

          {/* Role assignments */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-ink-900">Roles &amp; Organization Nodes</span>
              <button
                type="button"
                onClick={addRow}
                className="flex items-center gap-1.5 rounded-lg border border-brand px-3 py-1.5 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade"
              >
                <PlusIcon className="size-3.5" />
                Add Role
              </button>
            </div>

            {values.roles.map((row, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <label className="text-sm font-semibold text-ink-900">
                    Role<span className="text-[#ba1a1a]"> *</span>
                  </label>
                  <Select
                    className="w-full"
                    placeholder="Select Role"
                    status={rowError(i, 'role') ? 'error' : undefined}
                    value={row.role === '' ? undefined : row.role}
                    onChange={(v) => setRow(i, { role: v })}
                    options={roles.map((r) => ({ value: r.id, label: r.name }))}
                  />
                  {rowError(i, 'role') && (
                    <span className="text-xs font-medium text-[#ba1a1a]">{rowError(i, 'role')}</span>
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <label className="text-sm font-semibold text-ink-900">
                    Organization Level<span className="text-[#ba1a1a]"> *</span>
                  </label>
                  <TreeDropdown
                    placeholder={
                      values.organization === '' ? 'Select organization first' : 'Select Levels'
                    }
                    valueLabel={
                      row.orgNodes.length === 0
                        ? ''
                        : row.orgNodes.length <= 2
                          ? row.orgNodes.map((key) => orgNodeNameMap[key] || key).join(', ')
                          : `${row.orgNodes.length} levels selected`
                    }
                    invalid={Boolean(rowError(i, 'orgNodes'))}
                    disabled={values.organization === ''}
                    onClear={() => setRow(i, { orgNodes: [] })}
                    panelClassName="w-full"
                  >
                    {() => (
                      <OrgNodeTree
                        nodes={orgTree ?? []}
                        hideAreas
                        selected={new Set(row.orgNodes)}
                        onToggle={(key, _label, descendants) => {
                          const has = row.orgNodes.includes(key);
                          setRow(i, {
                            orgNodes: has
                              ? row.orgNodes.filter((x) => x !== key)
                              : // add this key, drop any of its descendants already picked
                                [...row.orgNodes.filter((x) => !descendants.includes(x)), key],
                          });
                        }}
                      />
                    )}
                  </TreeDropdown>
                  {rowError(i, 'orgNodes') && (
                    <span className="text-xs font-medium text-[#ba1a1a]">
                      {rowError(i, 'orgNodes')}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  aria-label={values.roles.length === 1 ? 'Clear role' : 'Remove role'}
                  onClick={() => removeRow(i)}
                  className="mt-7 flex size-11 shrink-0 items-center justify-center rounded-lg border border-line text-ink-secondary transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-500"
                >
                  <TrashIcon className="size-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="h-px w-full bg-line/30" />

          {/* Status toggle */}
          <div className="flex items-center justify-between gap-4 rounded-xl border border-line/20 bg-brand-shade p-4">
            <div className="flex items-center gap-4">
              <span className="size-3 shrink-0 rounded-full bg-brand" />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-ink-900">Status</span>
                <span className="text-sm text-ink-secondary">
                  User will be {isEdit ? 'saved' : 'created'} as{' '}
                  <span className="font-bold text-brand">{values.active ? 'Active' : 'Inactive'}</span>
                </span>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={values.active}
              onClick={() => setFieldValue('active', !values.active)}
              className={[
                'relative h-8 w-14 shrink-0 rounded-full transition-colors',
                values.active ? 'bg-brand' : 'bg-[#c2c6d6]',
              ].join(' ')}
            >
              <span
                className={[
                  'absolute top-1 size-6 rounded-full bg-white transition-all',
                  values.active ? 'left-[28px]' : 'left-1',
                ].join(' ')}
              />
            </button>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/admin/users')}
              className="rounded-lg border border-line px-[33px] py-[13px] text-sm font-semibold text-ink-secondary transition-colors hover:bg-brand-shade"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-brand px-10 py-3 text-sm font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving…' : isEdit ? 'Update User' : 'Save User'}
              <CheckCircleIcon className="size-[15px]" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
